import { useState, useEffect } from 'react'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Checkbox } from '../../ui/checkbox'
import { Input } from '../../ui/input'
import { Switch } from '../../ui/switch'
import { Label } from '../../ui/label'
import { FormError } from '../../ui/FormError'
import { salesService } from '../../../services/sales.service'
import { toast } from 'sonner'
import { useQueryClient, useQuery } from '@tanstack/react-query'

interface ReturnSaleModalProps {
  isOpen: boolean
  onClose: () => void
  sale: { id: string | number } | null
  initialType?: 'partial' | 'total'
}

export function ReturnSaleModal({ isOpen, onClose, sale, initialType = 'partial' }: ReturnSaleModalProps) {
  const queryClient = useQueryClient()
  
  const { data: saleDetail, isLoading } = useQuery({
    queryKey: ['saleDetail', sale?.id],
    queryFn: () => salesService.getSale(sale!.id),
    enabled: !!sale?.id && isOpen,
  })
  
  const formSchema = z.object({
    returnReason: z.string().min(1, 'El motivo de la devolución es obligatorio.'),
    bankReference: z.string().optional()
  })
  type FormValidate = z.infer<typeof formSchema>

  const { register, handleSubmit, setValue, setError, clearErrors, reset, watch, formState: { errors, isSubmitted } } = useForm<FormValidate>({
    resolver: zodResolver(formSchema),
    defaultValues: { returnReason: '', bankReference: '' },
    mode: 'onChange'
  })

  const returnReason = watch('returnReason')
  const bankReference = watch('bankReference')

  const [returnType, setReturnType] = useState<'partial' | 'total'>(initialType)
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [itemQuantities, setItemQuantities] = useState<Record<number, number>>({})
  const [itemRestock, setItemRestock] = useState<Record<number, boolean>>({})
  const [itemReasons, setItemReasons] = useState<Record<number, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const [isPendingBank, setIsPendingBank] = useState(false)
  const [pendingReturnId, setPendingReturnId] = useState<number | null>(null)
  const [hasAttemptedConfirm, setHasAttemptedConfirm] = useState(false)

  // Real-time validation for bank confirmation screen
  useEffect(() => {
    if (isPendingBank && hasAttemptedConfirm) {
      if (!bankReference) {
        setError('bankReference', { type: 'manual', message: 'El folio o referencia es obligatorio para pagos electrónicos.' })
      } else {
        clearErrors('bankReference')
      }
    }
  }, [bankReference, isPendingBank, hasAttemptedConfirm, setError, clearErrors])

  useEffect(() => {
    if (isOpen) {
      setReturnType(initialType)
      reset({ returnReason: '', bankReference: '' })
      setSelectedItems([])
      setItemQuantities({})
      setIsPendingBank(false)
      setPendingReturnId(null)
      setIsSubmitting(false)
    }
  }, [isOpen, initialType, sale, reset])

  useEffect(() => {
    if (saleDetail && saleDetail.items) {
      const initialRestock: Record<number, boolean> = {}
      const initialReasons: Record<number, string> = {}
      saleDetail.items.forEach((item) => {
        initialRestock[item.id] = true
        initialReasons[item.id] = ''
      })
      setItemRestock(initialRestock)
      setItemReasons(initialReasons)
    }
  }, [saleDetail])

  const toggleItemSelection = (itemId: number, maxQuantity: number) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter((id) => id !== itemId))
      const newQuantities = { ...itemQuantities }
      delete newQuantities[itemId]
      setItemQuantities(newQuantities)
    } else {
      setSelectedItems([...selectedItems, itemId])
      setItemQuantities({ ...itemQuantities, [itemId]: maxQuantity })
    }
  }

  const updateItemQuantity = (itemId: number, quantity: number) => {
    setItemQuantities({ ...itemQuantities, [itemId]: quantity })
  }

  const updateItemRestock = (itemId: number, restock: boolean) => {
    setItemRestock({ ...itemRestock, [itemId]: restock })
  }

  const updateItemReason = (itemId: number, reason: string) => {
    setItemReasons({ ...itemReasons, [itemId]: reason })
  }

  const calculateReturnTotal = () => {
    if (!saleDetail || !saleDetail.items) return 0
    if (returnType === 'total') return Number(saleDetail.amounts?.total || 0)

    return saleDetail.items
      .filter((item) => selectedItems.includes(item.id))
      .reduce((sum, item) => {
        const quantity = itemQuantities[item.id] || 0
        const itemPrice = Number(item.unit_price)
        return sum + (itemPrice * quantity)
      }, 0)
  }

  const processReturn = async (formData: FormValidate) => {
    if (!saleDetail || !sale) return
    
    if (returnType === 'partial' && selectedItems.length === 0) {
      toast.error('Debes seleccionar al menos un producto para devolver')
      return
    }

    setIsSubmitting(true)

    try {
      const generateUUID = () => {
        if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.getRandomValues === 'function') {
          return (1e7.toString() + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: any) =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
          )
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      };

      const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID 
        ? crypto.randomUUID() 
        : generateUUID()
        
      const isPartial = returnType === 'partial'
      
      const itemsPayload = isPartial 
        ? selectedItems.map((id) => ({
            sale_item_id: id,
            quantity: itemQuantities[id],
            restock_to_inventory: itemRestock[id] ?? true,
            reason: itemReasons[id] || formData.returnReason
          }))
        : saleDetail.items.map((i) => ({
             sale_item_id: i.id,
             quantity: Number(i.quantity) - Number(i.returned_quantity),
             restock_to_inventory: itemRestock[i.id] ?? true,
             reason: itemReasons[i.id] || formData.returnReason
          })).filter(i => i.quantity > 0)

      const payload = {
        idempotency_key: idempotencyKey,
        reason: formData.returnReason,
        items: itemsPayload
      }

      const response = await salesService.createReturn(sale.id, payload)
      
      const paymentMethod = saleDetail.payment?.method?.name || ''
      const isBankPayment = paymentMethod.toLowerCase().includes('tarjeta') || paymentMethod.toLowerCase().includes('transferencia')
      
      const returnId = response?.data?.return?.id || response?.data?.id

      if (isBankPayment && returnId) {
        setPendingReturnId(returnId)
        setIsPendingBank(true)
        setIsSubmitting(false)
        return
      }

      toast.success('Devolución procesada exitosamente')
      queryClient.invalidateQueries({ queryKey: ['saleDetail', sale.id] })
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      queryClient.invalidateQueries({ queryKey: ['register-context'] })
      queryClient.invalidateQueries({ queryKey: ['purchase-suggested-orders'] })
      onClose()
    } catch (error) {
      console.error(error)
      toast.error('Ocurrió un error al procesar la devolución')
    } finally {
      if (!isPendingBank) setIsSubmitting(false)
    }
  }

  const confirmBankReturn = async (formData: FormValidate) => {
    if (!pendingReturnId || !sale) return
    
    setHasAttemptedConfirm(true)
    
    // Si no ingresó referencia, detener
    if (!formData.bankReference) {
      setError('bankReference', { type: 'manual', message: 'El folio o referencia es obligatorio para pagos electrónicos.' })
      return
    }
    
    setIsSubmitting(true)
    
    try {
      await salesService.updateBankReturn(pendingReturnId, { reference: formData.bankReference })
      toast.success('Devolución confirmada con éxito')
      queryClient.invalidateQueries({ queryKey: ['saleDetail', sale.id] })
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      queryClient.invalidateQueries({ queryKey: ['register-context'] })
      queryClient.invalidateQueries({ queryKey: ['purchase-suggested-orders'] })
      onClose()
    } catch (error) {
      console.error(error)
      toast.error('Error al confirmar el reembolso en el sistema')
    } finally {
      setIsSubmitting(false)
    }
  }

  const cancelBankReturn = async () => {
    if (!pendingReturnId) return
    setIsSubmitting(true)
    
    try {
      await salesService.deleteBankReturn(pendingReturnId, { reason: 'Cancelado por el usuario o rechazado por el banco' })
      toast.info('La devolución bancaria fue cancelada')
      setIsPendingBank(false)
    } catch (error) {
      console.error(error)
      toast.error('Error al cancelar la devolución')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen || !sale) return null
  
  const paymentMethod = saleDetail?.payment?.method?.name || 'Desconocido'

  // BANK CONFIRMATION VIEW
  if (isPendingBank) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmación Bancaria</DialogTitle>
            <DialogDescription>
              La venta original se pagó con {paymentMethod}. Ingresa el folio de reversa o transferencia.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(confirmBankReturn)} className="space-y-4 py-4">
            <div className="rounded-lg bg-blue-50 p-4 border border-blue-100">
              <p className="text-sm font-medium text-blue-900">Total a reembolsar: ${calculateReturnTotal().toFixed(2)}</p>
            </div>

            <div className="space-y-2">
              <Label>Folio / Referencia ({paymentMethod}) <span className="text-red-500">*</span></Label>
              <Input 
                {...register('bankReference')}
                placeholder={paymentMethod.toLowerCase().includes('tarjeta') ? 'Ej. REEMBOLSO-BBVA-123' : 'Ej. SPEI-DEV-123'}
                disabled={isSubmitting}
              />
              {errors.bankReference && <FormError message={errors.bankReference.message} />}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? 'Procesando...' : 'Confirmar Devolución'}
              </Button>
              <Button
                onClick={cancelBankReturn}
                variant="destructive"
                className="flex-1"
                disabled={isSubmitting}
              >
                Rechazado (Cancelar)
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    )
  }

  // STANDARD RETURN VIEW
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {returnType === 'partial' ? 'Devolución Parcial' : 'Devolución Total'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Procesar devolución de productos vendidos
          </DialogDescription>
        </DialogHeader>
        
        {isLoading || !saleDetail ? (
          <div className="py-20 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(processReturn)} className="space-y-6">
            <div className="rounded-lg border border-border bg-card p-4 flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-card-foreground">Ticket: {saleDetail.ticket_number}</p>
                <p className="text-sm text-card-foreground">Cliente: {saleDetail.customer?.name || 'Mostrador'}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-card-foreground">Método de pago:</p>
                <p className="text-sm font-medium text-card-foreground">{paymentMethod}</p>
              </div>
            </div>

            {/* Return Type Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">Tipo de devolución:</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setReturnType('partial')}
                  className={`rounded-lg border-2 p-3 transition-all ${
                    returnType === 'partial'
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-card-foreground hover:bg-table-row-hover'
                  }`}
                >
                  <p className="text-sm font-medium">Parcial</p>
                  <p className="text-xs text-card-description">Seleccionar productos</p>
                </button>
                <button
                  onClick={() => setReturnType('total')}
                  className={`rounded-lg border-2 p-3 transition-all ${
                    returnType === 'total'
                      ?  'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-card-foreground hover:bg-table-row-hover'
                  }`}
                >
                  <p className="text-sm font-medium">Total</p>
                  <p className="text-xs text-card-description">Todos los productos</p>
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-card-foreground">
                {returnType === 'partial' ? 'Selecciona los productos a devolver:' : 'Productos que serán devueltos:'} <span className="text-red-500">*</span>
              </label>
              <div className="divide-y divide-border rounded-lg border border-border">
                {saleDetail.items.map((item) => {
                  const availableQty = Number(item.quantity) - Number(item.returned_quantity)
                  if (availableQty <= 0) return null
                  
                  const isSelected = returnType === 'total' || selectedItems.includes(item.id)

                  return (
                    <div key={item.id} className={`p-4 transition-colors ${isSelected ? 'bg-primary/10' : 'bg-card' }`}>
                      <div className="flex items-start gap-4">
                        {returnType === 'partial' && (
                          <Checkbox
                            id={`item-${item.id}`}
                            checked={isSelected}
                            onCheckedChange={() => toggleItemSelection(item.id, availableQty)}
                            className="mt-1"
                          />
                        )}
                        
                        <div className="flex-1 space-y-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <label
                                htmlFor={`item-${item.id}`}
                                className="cursor-pointer text-sm font-medium text-card-foreground"
                              >
                                {item.product_name}
                              </label>
                              <p className="mt-1 text-xs text-card-description">
                                Precio unitario: ${Number(item.unit_price).toFixed(2)} | Disponible para devolver: {availableQty} uds
                              </p>
                            </div>
                            
                            {isSelected && returnType === 'partial' && (
                              <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-card-foreground">Cant:</label>
                                <Input
                                  type="number"
                                  min={1}
                                  max={availableQty}
                                  value={itemQuantities[item.id] || availableQty}
                                  onChange={(e) =>
                                    updateItemQuantity(
                                      item.id,
                                      Math.min(parseInt(e.target.value) || 1, availableQty)
                                    )
                                  }
                                  className="h-8 w-20 text-sm"
                                />
                              </div>
                            )}
                            {isSelected && returnType === 'total' && (
                              <p className="text-sm font-medium text-card-foreground">{availableQty} uds</p>
                            )}
                          </div>

                          {/* Extended Product Return Options */}
                          {isSelected && (
                            <div className="flex items-center justify-between bg-card p-2 rounded border border-border">
                               <div className="flex items-center justify-between bg-card p-2 rounded border border-border">
                                <div className="space-y-0.5">
                                  <Label className="text-xs font-medium text-card-foreground">Regresar a Inventario</Label>
                                  <p className="text-[10px] text-card-description">¿El producto está en buen estado?</p>
                                </div>
                                <Switch 
                                  checked={itemRestock[item.id] ?? true} 
                                  onCheckedChange={(checked) => updateItemRestock(item.id, checked)}
                                />
                              </div>
                              
                              <div>
                                <Input 
                                  placeholder="Motivo específico (opcional)" 
                                  className="h-10 text-xs"
                                  value={itemReasons[item.id] || ''}
                                  onChange={(e) => updateItemReason(item.id, e.target.value)}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              {isSubmitted && returnType === 'partial' && selectedItems.length === 0 && (
                <div className="pt-1">
                  <FormError message="Debes seleccionar al menos un producto para devolver." />
                </div>
              )}
            </div>

            {/* General Return Reason */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-card-foreground">Motivo de la devolución: <span className="text-red-500">*</span></label>
              <Input
                {...register('returnReason')}
                placeholder="Escribe el motivo o selecciona una opción..."
                className="w-full"
              />
              {errors.returnReason && <FormError message={errors.returnReason.message} />}
              <div className="flex flex-wrap gap-2 mt-2">
                {['Solicitud del cliente', 'Producto dañado', 'Cobro duplicado'].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setValue('returnReason', tag, { shouldValidate: true })}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      returnReason === tag 
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-card-foreground border-border'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Return Total */}
            <div className="rounded-lg bg-primary/10 p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-primary/90">Total a reembolsar:</span>
                <span className="text-2xl font-semibold text-primary/90">
                  ${calculateReturnTotal().toFixed(2)}
                </span>
              </div>
              {(paymentMethod.toLowerCase().includes('tarjeta') || paymentMethod.toLowerCase().includes('transferencia')) && (
                <p className="text-xs text-primary/70 mt-1">
                  * Requerirá confirmación de folio en el siguiente paso.
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? 'Procesando...' : (paymentMethod === 'Efectivo' ? 'Completar Devolución' : 'Continuar al Reembolso')}
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
